package diana.soleil.design.iraniancanadienbazzar.locale

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.common.deviceLanguageCode
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.koin.dsl.module

/**
 * Holds the app's active language (EN/FR/FA) as observable state, **persisted** via [AppPreferences]
 * so it survives restarts. First run defaults to the device locale (constrained to EN/FR/FA). `App()`
 * feeds it to `AppTheme`, which sets the layout direction (RTL for Farsi) and the language used to
 * resolve `LocalizedText` business content. Signed-in accounts mirror the choice to
 * `users/{uid}.preferredLanguage` via `UserPreferencesSync`.
 *
 * Chrome strings switch live too: `App()` provides [LocalAppLocale] (the JetBrains-documented
 * per-platform locale rebind) keyed on this language, so `stringResource` re-resolves from
 * `values-fr/`/`values-fa/` immediately — no device-locale change or restart needed.
 */
class LocaleController(private val preferences: AppPreferences) {
    // Stored choice wins; on first run fall back to the device language (constrained to EN/FR/FA).
    private val _language = MutableStateFlow(
        Language.fromCode(preferences.languageCode ?: deviceLanguageCode()),
    )
    val language: StateFlow<Language> = _language.asStateFlow()

    /**
     * True once someone has picked a language **on this device** (Settings → Language & Region).
     * Until then the active language is only a guess from the device locale, so it is safe for a
     * signing-in account to seed it. After that it is a deliberate choice and `UserPreferencesSync`
     * must not overwrite it — owner bug 2026-09-20: signing in flipped the whole UI to Persian
     * mid-session for users who had deliberately set English.
     */
    val hasExplicitChoice: Boolean get() = preferences.languageCode != null

    fun setLanguage(language: Language) {
        preferences.languageCode = language.code
        _language.value = language
    }
}

val localeModule = module {
    single { LocaleController(get()) }
}
